import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useSettingStore } from '@/stores'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Copy, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'

const WebSetting = observer(() => {
  const { tenantId } = useParams<{ tenantId: string }>()
  const settingStore = useSettingStore()
  const [enabled, setEnabled] = useState(false)
  const [saving, setSaving] = useState(false)

  const chatBaseUrl = import.meta.env.VITE_HOGE_AGENT_URL || window.location.origin
  const chatUrl = `${chatBaseUrl}/webchat/${tenantId}`

  const closedSize = 96
  const openWidth = 380
  const openHeight = 620

  const snippet = `<iframe
  id="hoge-webchat-frame"
  src="${chatUrl}"
  title="Webチャット"
  allow="clipboard-write"
  style="position:fixed;right:0;bottom:0;width:${closedSize}px;height:${closedSize}px;border:0;background:transparent;z-index:2147483000;pointer-events:auto;"
></iframe>
<script>
(function () {
  var frame = document.getElementById('hoge-webchat-frame');
  var base = ${JSON.stringify(chatBaseUrl)};
  window.addEventListener('message', function (event) {
    if (event.origin !== new URL(base).origin) return;
    var data = event.data || {};
    if (data.type !== 'hoge-webchat:resize') return;
    frame.style.width = (data.open ? Math.min(${openWidth}, window.innerWidth) : ${closedSize}) + 'px';
    frame.style.height = (data.open ? Math.min(${openHeight}, window.innerHeight) : ${closedSize}) + 'px';
  });
})();
</script>`

  const loadWebChatSetting = useCallback(async () => {
    const success = await settingStore.getWebChatSetting(tenantId!)
    if (success && settingStore.webChatSetting) {
      setEnabled(settingStore.webChatSetting.enabled)
    }
  }, [settingStore, tenantId])

  useEffect(() => {
    if (tenantId) {
      loadWebChatSetting()
    }
  }, [loadWebChatSetting, tenantId])

  const handleToggle = async (next: boolean) => {
    setEnabled(next)
    setSaving(true)
    try {
      const success = await settingStore.updateWebChatSetting(tenantId!, next)
      if (!success) {
        setEnabled(!next)
      }
    } finally {
      setSaving(false)
    }
  }

  const copySnippet = () => {
    navigator.clipboard.writeText(snippet)
    toast.success('iframeタグをコピーしました')
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Web設定</h1>
        <p className="text-muted-foreground">
          Webチャット機能の有効・無効と、サイトへの埋め込み設定を行います
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle>Webチャット</CardTitle>
              <CardDescription>
                有効にすると、任意のサイトにiframeで組み込めるWebチャットを公開します
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="webchat-enabled" className="text-sm">
                {enabled ? '有効' : '無効'}
              </Label>
              <Switch
                id="webchat-enabled"
                checked={enabled}
                disabled={saving}
                onCheckedChange={(checked) => void handleToggle(checked)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertDescription>
              WebチャットはLINEを利用しないサイト訪問者向けのチャット窓口です。有効化すると
              hogeAgent にゲストとして接続され、対応スタッフがLINEゲストと同様に応対できます。
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label>チャットURL</Label>
            <div className="flex gap-2">
              <div className="flex-1 rounded-md border bg-muted px-3 py-2 font-mono text-sm break-all">
                {chatUrl}
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => window.open(chatUrl, '_blank', 'noopener,noreferrer')}
                aria-label="チャットURLを開く"
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {enabled && (
        <Card>
          <CardHeader>
            <CardTitle>サイトへの組み込み</CardTitle>
            <CardDescription>
              以下のiframeタグを、チャットを設置したいサイトのHTML（&lt;body&gt; の閉じタグ直前など）に貼り付けてください
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>iframeタグ</Label>
                <Button variant="outline" size="sm" onClick={copySnippet}>
                  <Copy className="mr-2 h-4 w-4" />
                  コピー
                </Button>
              </div>
              <pre className="max-h-[420px] overflow-auto rounded-md border bg-muted p-3 text-xs">
                <code>{snippet}</code>
              </pre>
            </div>

            <Alert>
              <AlertDescription>
                右下にスタートアイコン（フローティングボタン）が表示されます。クリックするとチャット
                インターフェースが開きます。タグに含まれるスクリプトがiframeのサイズを自動調整するため、
                閉じている間もリンクなどの操作を妨げません。
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}
    </div>
  )
})

export default WebSetting
